import { useEffect, useMemo, useState } from "react";
import type { CustomApp } from "@clickdialog/product-registry";
import type { PlatformApiClient, CustomAppPublicToken } from "./api";
import type { OrganizationContext } from "./types";
import { ActionBar, FormLayout, PageHeader, ProductSubnav, SectionHeader } from "./adaptive/primitives";
import { Mesa28Analytics } from "./Mesa28Analytics";

interface Mesa28MenuItem {
  id: string;
  week: number;
  date: string;
  dish: string;
  category: string;
  description: string;
  price: number;
  image: string;
  visible: boolean;
}

interface Mesa28Choice {
  id: string;
  name: string;
  image: string;
  visible: boolean;
}

interface Mesa28EverydayOffer {
  enabled: boolean;
  title: string;
  heroTitle: string;
  subtitle: string;
  price: number;
  cutoff: string;
  deliveryText: string;
  heroImage: string;
  sauces: Mesa28Choice[];
  sides: Mesa28Choice[];
}

interface Mesa28ConfigV2 {
  kind: "mesa28";
  version: 2;
  landing: {
    eyebrow: string;
    title: string;
    subtitle: string;
    intro: string;
    keyTitle: string;
    keyText: string;
    footer: string;
  };
  whatsapp: { number: string; message: string };
  rules: string[];
  benefits: string[];
  menu: Mesa28MenuItem[];
  everydayOffer: Mesa28EverydayOffer;
}

type ChoiceGroup = "sauces" | "sides";
type LocalTab = "milanesas" | "menu" | "landing" | "analytics" | "settings";

const DEFAULT_RULES = [
  "Cada día tiene un único plato del día.",
  "Consultá el menú mensual y elegí los días que te interesan.",
  "Los pedidos se coordinan directamente por WhatsApp.",
  "La entrega se realiza al comercio a la hora del almuerzo.",
];

const DEFAULT_BENEFITS = [
  "Ahorro de tiempo",
  "Mejor organización",
  "Comida rica y bien presentada",
  "Entrega directa al comercio",
];

const DEFAULT_SAUCES: Mesa28Choice[] = [
  { id: "queso-tomate", name: "Queso cremoso y tomate", image: "", visible: true },
  { id: "roquefort", name: "Salsa roquefort", image: "", visible: true },
  { id: "morrones", name: "Salsa de morrones", image: "", visible: true },
  { id: "napolitana", name: "Napolitana", image: "", visible: true },
  { id: "champinones", name: "Salsa de champiñones", image: "", visible: true },
];

const DEFAULT_SIDES: Mesa28Choice[] = [
  { id: "papas-verduras", name: "Papas horneadas con verduras extras", image: "", visible: true },
  { id: "pure-casero", name: "Puré casero", image: "", visible: true },
];

function text(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function object(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function normalizeMenu(value: unknown): Mesa28MenuItem[] {
  if (!Array.isArray(value)) return [];
  return value.map((raw, index) => {
    const item = object(raw);
    return {
      id: text(item.id, `dish-${index + 1}`),
      week: Number.isSafeInteger(Number(item.week)) ? Math.max(1, Number(item.week)) : 1,
      date: text(item.date),
      dish: text(item.dish, "Nuevo plato"),
      category: text(item.category, "Plato del día"),
      description: text(item.description, text(item.desc)),
      price: Number.isFinite(Number(item.price)) ? Math.max(0, Number(item.price)) : 0,
      image: text(item.image),
      visible: item.visible !== false,
    };
  }).sort((a, b) => a.date.localeCompare(b.date) || a.week - b.week);
}

function normalizeChoices(value: unknown, defaults: Mesa28Choice[]): Mesa28Choice[] {
  if (!Array.isArray(value) || value.length === 0) return defaults.map((item) => ({ ...item }));
  return value.map((raw, index) => {
    const item = object(raw);
    const fallback = defaults[index] ?? { id: `option-${index + 1}`, name: `Opción ${index + 1}`, image: "", visible: true };
    return {
      id: text(item.id, fallback.id),
      name: text(item.name, fallback.name),
      image: text(item.image),
      visible: item.visible !== false,
    };
  });
}

function normalizeConfig(app: CustomApp): Mesa28ConfigV2 {
  const raw = object(app.configuration);
  const landing = object(raw.landing);
  const whatsapp = object(raw.whatsapp);
  const everyday = object(raw.everydayOffer);
  return {
    kind: "mesa28",
    version: 2,
    landing: {
      eyebrow: text(landing.eyebrow, "MESA 28"),
      title: text(landing.title, "Almuerzos programados, directo a tu trabajo"),
      subtitle: text(landing.subtitle, "Comida casera, fresca y hecha con planificación."),
      intro: text(landing.intro, "Para comercios y equipos que trabajan de corrido al mediodía."),
      keyTitle: text(landing.keyTitle, "La clave es planificar"),
      keyText: text(landing.keyText, "Planificamos con anticipación para cocinar mejor, reducir desperdicio y mantener una excelente relación precio-calidad."),
      footer: text(landing.footer, "Vos trabajás. Nosotros llevamos el almuerzo."),
    },
    whatsapp: {
      number: text(whatsapp.number),
      message: text(whatsapp.message, "Hola MESA 28, vi el menú y quería hacer una consulta."),
    },
    rules: Array.isArray(raw.rules) ? raw.rules.map(String).filter(Boolean) : [...DEFAULT_RULES],
    benefits: Array.isArray(raw.benefits) ? raw.benefits.map(String).filter(Boolean) : [...DEFAULT_BENEFITS],
    menu: normalizeMenu(raw.menu),
    everydayOffer: {
      enabled: everyday.enabled === true,
      title: text(everyday.title, "Milanesas todos los días"),
      heroTitle: text(everyday.heroTitle, "Milanesas todos los días, a tu gusto"),
      subtitle: text(everyday.subtitle, "Elegí tu salsa, tu acompañamiento y pedí hoy hasta las 10:30 para recibirlo al almuerzo."),
      price: Number.isFinite(Number(everyday.price)) ? Math.max(0, Number(everyday.price)) : 10000,
      cutoff: /^\d{2}:\d{2}$/.test(text(everyday.cutoff)) ? text(everyday.cutoff) : "10:30",
      deliveryText: text(everyday.deliveryText, "Entrega al horario del almuerzo"),
      heroImage: text(everyday.heroImage),
      sauces: normalizeChoices(everyday.sauces, DEFAULT_SAUCES),
      sides: normalizeChoices(everyday.sides, DEFAULT_SIDES),
    },
  };
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(value || 0);
}

function formatDate(date: string): string {
  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return date || "Sin fecha";
  return new Intl.DateTimeFormat("es-AR", { weekday: "short", day: "2-digit", month: "short" }).format(parsed);
}

function nextMondayAfter(lastDate: string): Date {
  const date = lastDate ? new Date(`${lastDate}T12:00:00`) : new Date();
  date.setDate(date.getDate() + 1);
  while (date.getDay() !== 1) date.setDate(date.getDate() + 1);
  return date;
}

function iso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function publicHost(): string {
  if (typeof window !== "undefined" && window.location.hostname.startsWith("app.staging.")) return "https://go.staging.clickdialog.com";
  return "https://go.clickdialog.com";
}

function OfferPhoto({
  path,
  label,
  uploading,
  onUpload,
}: {
  path: string;
  label: string;
  uploading: boolean;
  onUpload: (file: File) => void;
}) {
  return <div className="mesa-photo-preview mesa-v2-photo">
    {path ? <img src={`${publicHost()}${path}`} alt="" /> : <span>Sin foto</span>}
    <label>{uploading ? "Subiendo…" : label}<input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} onChange={(event) => { const file = event.currentTarget.files?.[0]; if (file) onUpload(file); }} /></label>
  </div>;
}

function ChoiceThumb({ choice }: { choice: Mesa28Choice }) {
  return <span className="mesa-choice-thumb" aria-hidden="true">
    {choice.image ? <img src={`${publicHost()}${choice.image}`} alt="" /> : <span>{choice.name.slice(0, 1).toUpperCase()}</span>}
  </span>;
}

export function Mesa28AdminV2({
  organization,
  app: initialApp,
  api,
  onAppChange,
}: {
  organization: OrganizationContext;
  app: CustomApp;
  api: PlatformApiClient;
  onAppChange: (app: CustomApp) => void;
}) {
  const [app, setApp] = useState(initialApp);
  const [draft, setDraft] = useState<Mesa28ConfigV2>(() => normalizeConfig(initialApp));
  const [tab, setTab] = useState<LocalTab>("milanesas");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [publicToken, setPublicToken] = useState<CustomAppPublicToken | null>(null);
  const [selectedMenuItemId, setSelectedMenuItemId] = useState<string | null>(null);
  const [selectedChoice, setSelectedChoice] = useState<{ group: ChoiceGroup; id: string } | null>(null);

  useEffect(() => {
    setApp(initialApp);
    setDraft(normalizeConfig(initialApp));
  }, [initialApp]);

  useEffect(() => {
    api.customPublicToken(organization.organization.id, app.id)
      .then((result) => setPublicToken(result.publicToken))
      .catch(() => setPublicToken(null));
  }, [api, organization.organization.id, app.id]);

  useEffect(() => {
    if (selectedMenuItemId && !draft.menu.some((item) => item.id === selectedMenuItemId)) {
      setSelectedMenuItemId(null);
    }
  }, [draft.menu, selectedMenuItemId]);

  useEffect(() => {
    if (!selectedChoice) return;
    const exists = draft.everydayOffer[selectedChoice.group].some((item) => item.id === selectedChoice.id);
    if (!exists) setSelectedChoice(null);
  }, [draft.everydayOffer, selectedChoice]);

  const canonicalDraft = useMemo(() => normalizeConfig(app), [app]);
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(canonicalDraft), [draft, canonicalDraft]);

  const weeks = useMemo(() => {
    const map = new Map<number, Mesa28MenuItem[]>();
    draft.menu.forEach((item) => {
      const current = map.get(item.week) ?? [];
      current.push(item);
      map.set(item.week, current);
    });
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [draft.menu]);

  const selectedMenuItem = useMemo(
    () => draft.menu.find((item) => item.id === selectedMenuItemId) ?? null,
    [draft.menu, selectedMenuItemId],
  );

  const selectedChoiceItem = useMemo(() => {
    if (!selectedChoice) return null;
    return draft.everydayOffer[selectedChoice.group].find((item) => item.id === selectedChoice.id) ?? null;
  }, [draft.everydayOffer, selectedChoice]);

  function updateItem(id: string, patch: Partial<Mesa28MenuItem>) {
    setDraft((current) => ({
      ...current,
      menu: current.menu.map((item) => item.id === id ? { ...item, ...patch } : item),
    }));
  }

  function removeItem(id: string) {
    if (!window.confirm("¿Eliminar este plato de la planificación?")) return;
    setDraft((current) => ({ ...current, menu: current.menu.filter((item) => item.id !== id) }));
  }

  function addWeek() {
    const sorted = [...draft.menu].filter((item) => item.date).sort((a, b) => a.date.localeCompare(b.date));
    const start = nextMondayAfter(sorted.at(-1)?.date ?? "");
    const week = Math.max(0, ...draft.menu.map((item) => item.week)) + 1;
    const items: Mesa28MenuItem[] = Array.from({ length: 5 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return {
        id: `dish-${Date.now()}-${index}`,
        week,
        date: iso(date),
        dish: "Nuevo plato",
        category: "Plato del día",
        description: "",
        price: 0,
        image: "",
        visible: true,
      };
    });
    setDraft((current) => ({ ...current, menu: [...current.menu, ...items] }));
    setSelectedMenuItemId(items[0]?.id ?? null);
    setMessage(`Semana ${week} agregada.`);
  }

  function duplicateWeek(week: number) {
    const source = draft.menu.filter((item) => item.week === week).sort((a, b) => a.date.localeCompare(b.date));
    if (!source.length) return;
    const lastDate = [...draft.menu].filter((item) => item.date).sort((a, b) => a.date.localeCompare(b.date)).at(-1)?.date ?? "";
    const start = nextMondayAfter(lastDate);
    const targetWeek = Math.max(0, ...draft.menu.map((item) => item.week)) + 1;
    const copies = source.map((item, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return { ...item, id: `dish-${Date.now()}-${index}`, week: targetWeek, date: iso(date) };
    });
    setDraft((current) => ({ ...current, menu: [...current.menu, ...copies] }));
    setSelectedMenuItemId(copies[0]?.id ?? null);
    setMessage(`Semana ${week} duplicada como semana ${targetWeek}.`);
  }

  function updateChoice(group: ChoiceGroup, id: string, patch: Partial<Mesa28Choice>) {
    setDraft((current) => ({
      ...current,
      everydayOffer: {
        ...current.everydayOffer,
        [group]: current.everydayOffer[group].map((item) => item.id === id ? { ...item, ...patch } : item),
      },
    }));
  }

  function addChoice(group: ChoiceGroup) {
    const noun = group === "sauces" ? "salsa" : "acompanamiento";
    const item: Mesa28Choice = { id: `${noun}-${Date.now()}`, name: group === "sauces" ? "Nueva salsa" : "Nuevo acompañamiento", image: "", visible: true };
    setDraft((current) => ({
      ...current,
      everydayOffer: { ...current.everydayOffer, [group]: [...current.everydayOffer[group], item] },
    }));
    setSelectedChoice({ group, id: item.id });
  }

  function removeChoice(group: ChoiceGroup, id: string) {
    if (!window.confirm("¿Eliminar esta opción?")) return;
    setDraft((current) => ({
      ...current,
      everydayOffer: { ...current.everydayOffer, [group]: current.everydayOffer[group].filter((item) => item.id !== id) },
    }));
  }

  async function upload(key: string, file: File, apply: (path: string) => void) {
    setUploading(key);
    setMessage("");
    try {
      const asset = await api.uploadCustomAsset(organization.organization.slug, app.appKey, file);
      apply(asset.path);
      setMessage("Foto cargada. Guardá los cambios para publicarla.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "No se pudo cargar la foto.");
    } finally {
      setUploading(null);
    }
  }

  async function save() {
    if (!dirty || saving) return;
    setSaving(true);
    setMessage("");
    try {
      const result = await api.updateCustomApp(organization.organization.id, app.id, {
        configuration: draft as unknown as Readonly<Record<string, unknown>>,
        expectedVersion: app.version,
      });
      setApp(result.customApp);
      setDraft(normalizeConfig(result.customApp));
      onAppChange(result.customApp);
      setMessage("Cambios guardados y publicados.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "No se pudieron guardar los cambios.");
    } finally {
      setSaving(false);
    }
  }

  async function createPublicLink() {
    setMessage("");
    try {
      const result = await api.ensureCustomPublicToken(organization.organization.id, app.id);
      setPublicToken(result.publicToken);
      setMessage("Enlace público permanente listo.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "No se pudo generar el enlace público.");
    }
  }

  async function copyPublicUrl(publicUrl: string) {
    if (!navigator.clipboard?.writeText) {
      setMessage("No se pudo copiar el enlace automáticamente.");
      return;
    }
    try {
      await navigator.clipboard.writeText(publicUrl);
      setMessage("Enlace copiado.");
    } catch {
      setMessage("No se pudo copiar el enlace automáticamente.");
    }
  }

  function renderChoiceSection(group: ChoiceGroup, title: string, addLabel: string) {
    const choices = draft.everydayOffer[group];
    const selected = selectedChoice?.group === group ? selectedChoiceItem : null;
    return <section className="mesa-choice-section" aria-labelledby={`mesa-${group}-title`}>
      <SectionHeader
        titleId={`mesa-${group}-title`}
        title={title}
        actions={<button type="button" className="mesa-text-action" onClick={() => addChoice(group)}>+ {addLabel}</button>}
      />
      <div className="mesa-choice-workspace">
        <div className="mesa-choice-list" role="list">
          {choices.map((choice) => {
            const active = selectedChoice?.group === group && selectedChoice.id === choice.id;
            return <div className={`mesa-choice-row ${active ? "is-selected" : ""}`} role="listitem" key={choice.id}>
              <button
                type="button"
                className="mesa-choice-row-main"
                onClick={() => setSelectedChoice({ group, id: choice.id })}
                aria-pressed={active}
              >
                <ChoiceThumb choice={choice} />
                <span className="mesa-choice-copy">
                  <strong>{choice.name || "Sin nombre"}</strong>
                  <small>{choice.image ? "Con foto" : "Sin foto"}</small>
                </span>
                <span className={`mesa-state-label ${choice.visible ? "is-live" : "is-muted"}`}>{choice.visible ? "Visible" : "Oculta"}</span>
                <span className="mesa-row-chevron" aria-hidden="true">›</span>
              </button>
            </div>;
          })}
          {choices.length === 0 && <div className="mesa-inline-empty">No hay opciones todavía.</div>}
        </div>

        <div className="mesa-detail-workspace">
          {selected ? <>
            <div className="mesa-detail-heading">
              <div><span className="mesa-detail-eyebrow">Editar opción</span><h4>{selected.name || "Sin nombre"}</h4></div>
              <button type="button" className="mesa-detail-close" onClick={() => setSelectedChoice(null)} aria-label={`Cerrar edición de ${selected.name}`}>×</button>
            </div>
            <OfferPhoto
              path={selected.image}
              label="Cambiar foto"
              uploading={uploading === `${group}-${selected.id}`}
              onUpload={(file) => void upload(`${group}-${selected.id}`, file, (path) => updateChoice(group, selected.id, { image: path }))}
            />
            <label>Nombre<input value={selected.name} onChange={(e) => updateChoice(group, selected.id, { name: e.target.value })} /></label>
            <div className="mesa-detail-footer">
              <label className="mesa-toggle"><input type="checkbox" checked={selected.visible} onChange={(e) => updateChoice(group, selected.id, { visible: e.target.checked })} /> Visible en el sitio</label>
              <button type="button" className="danger" onClick={() => removeChoice(group, selected.id)}>Eliminar</button>
            </div>
          </> : <div className="mesa-detail-placeholder"><strong>Seleccioná una opción</strong><span>Podés revisar todas las opciones sin abrir sus campos de edición.</span></div>}
        </div>
      </div>
    </section>;
  }

  function renderMenuEditor(item: Mesa28MenuItem) {
    return <div className="mesa-menu-detail" aria-label={`Editar ${item.dish}`}>
      <div className="mesa-detail-heading">
        <div>
          <span className="mesa-detail-eyebrow">{formatDate(item.date)}</span>
          <h4>{item.dish || "Sin nombre"}</h4>
        </div>
        <button type="button" className="mesa-detail-close" onClick={() => setSelectedMenuItemId(null)} aria-label={`Cerrar edición de ${item.dish}`}>×</button>
      </div>
      <div className="mesa-menu-detail-body">
        <OfferPhoto path={item.image} label="Cambiar foto" uploading={uploading === item.id} onUpload={(file) => void upload(item.id, file, (path) => updateItem(item.id, { image: path }))} />
        <FormLayout className="mesa-form-grid mesa-menu-detail-fields">
          <label>Fecha<input type="date" value={item.date} onChange={(e) => updateItem(item.id, { date: e.target.value })} /></label>
          <label>Plato<input value={item.dish} onChange={(e) => updateItem(item.id, { dish: e.target.value })} /></label>
          <label>Categoría<input value={item.category} onChange={(e) => updateItem(item.id, { category: e.target.value })} /></label>
          <label>Precio ARS<input type="number" min="0" step="100" value={item.price} onChange={(e) => updateItem(item.id, { price: Number(e.target.value) || 0 })} /></label>
          <label className="mesa-field-wide">Descripción<textarea rows={3} value={item.description} onChange={(e) => updateItem(item.id, { description: e.target.value })} /></label>
        </FormLayout>
      </div>
      <div className="mesa-detail-footer">
        <label className="mesa-toggle"><input type="checkbox" checked={item.visible} onChange={(e) => updateItem(item.id, { visible: e.target.checked })} /> Visible en el sitio</label>
        <button type="button" className="danger" onClick={() => removeItem(item.id)}>Eliminar plato</button>
      </div>
    </div>;
  }

  const publicUrl = publicToken ? `${publicHost()}/t/${publicToken.token}` : "";
  const tabs = [
    { key: "milanesas", label: "Oferta diaria" },
    { key: "menu", label: "Calendario" },
    { key: "landing", label: "Sitio público" },
    { key: "analytics", label: "Rendimiento" },
    { key: "settings", label: "Ajustes" },
  ] as const;

  const publicationLabel = saving ? "Guardando…" : dirty ? "Cambios sin guardar" : "Publicado";
  const publicationDetail = saving ? "Sincronizando con ClickDialog" : dirty ? "Estos cambios todavía no están en la web" : `Versión ${app.version} sincronizada`;

  return (
    <div className="mesa-admin mesa-v2-admin">
      <PageHeader
        className="mesa-admin-header mesa-v2-page-header"
        eyebrow="Aplicación personalizada"
        title="MESA 28"
        description="Oferta diaria, calendario y sitio público en un solo espacio de trabajo."
        actions={<div className="mesa-v2-header-controls">
          <div className={`mesa-publication-state ${dirty ? "is-dirty" : "is-synced"} ${saving ? "is-saving" : ""}`} role="status" aria-live="polite">
            <span className="mesa-publication-dot" aria-hidden="true" />
            <span><strong>{publicationLabel}</strong><small>{publicationDetail}</small></span>
          </div>
          <ActionBar className="mesa-admin-actions" sticky="never">
            {publicUrl
              ? <a className="mesa-button secondary" href={publicUrl} target="_blank" rel="noreferrer">Abrir sitio</a>
              : <button type="button" className="mesa-button secondary" onClick={createPublicLink}>Crear enlace</button>}
            <button className="mesa-button primary" onClick={save} disabled={saving || !dirty}>{saving ? "Guardando…" : "Guardar y publicar"}</button>
          </ActionBar>
        </div>}
      />

      {message && <div className="mesa-notice mesa-v2-notice" role="status">{message}</div>}

      <ProductSubnav
        className="mesa-tabs mesa-v2-tabs"
        items={tabs}
        activeKey={tab}
        ariaLabel="Secciones MESA 28"
        onSelect={(key) => setTab(key as LocalTab)}
      />

      {tab === "milanesas" && <section className="mesa-panel mesa-v2-offer">
        <SectionHeader
          className="mesa-panel-head"
          title="Oferta diaria"
          description="La propuesta que puede pedirse cualquier día: precio, horario, salsa y acompañamiento."
          actions={<label className="mesa-toggle mesa-v2-enable"><input type="checkbox" checked={draft.everydayOffer.enabled} onChange={(e) => setDraft((current) => ({ ...current, everydayOffer: { ...current.everydayOffer, enabled: e.target.checked } }))} /> {draft.everydayOffer.enabled ? "Oferta activa" : "Oferta pausada"}</label>}
        />

        <div className="mesa-offer-ops-strip" aria-label="Datos operativos de la oferta diaria">
          <label><span>Precio</span><input type="number" min="0" step="100" value={draft.everydayOffer.price} onChange={(e) => setDraft((current) => ({ ...current, everydayOffer: { ...current.everydayOffer, price: Number(e.target.value) || 0 } }))} /><small>{formatMoney(draft.everydayOffer.price)}</small></label>
          <label><span>Pedidos hasta</span><input type="time" value={draft.everydayOffer.cutoff} onChange={(e) => setDraft((current) => ({ ...current, everydayOffer: { ...current.everydayOffer, cutoff: e.target.value } }))} /></label>
          <label className="mesa-offer-delivery"><span>Entrega</span><input value={draft.everydayOffer.deliveryText} onChange={(e) => setDraft((current) => ({ ...current, everydayOffer: { ...current.everydayOffer, deliveryText: e.target.value } }))} /></label>
        </div>

        <div className="mesa-v2-offer-general">
          <OfferPhoto path={draft.everydayOffer.heroImage} label="Cambiar foto principal" uploading={uploading === "offer-hero"} onUpload={(file) => void upload("offer-hero", file, (path) => setDraft((current) => ({ ...current, everydayOffer: { ...current.everydayOffer, heroImage: path } })))} />
          <div className="mesa-offer-copy">
            <span className="mesa-detail-eyebrow">Contenido visible</span>
            <FormLayout className="mesa-form-grid mesa-v2-general-fields">
              <label>Nombre interno / sección<input value={draft.everydayOffer.title} onChange={(e) => setDraft((current) => ({ ...current, everydayOffer: { ...current.everydayOffer, title: e.target.value } }))} /></label>
              <label>Título principal<input value={draft.everydayOffer.heroTitle} onChange={(e) => setDraft((current) => ({ ...current, everydayOffer: { ...current.everydayOffer, heroTitle: e.target.value } }))} /></label>
              <label className="mesa-field-wide">Texto principal<textarea rows={3} value={draft.everydayOffer.subtitle} onChange={(e) => setDraft((current) => ({ ...current, everydayOffer: { ...current.everydayOffer, subtitle: e.target.value } }))} /></label>
            </FormLayout>
          </div>
        </div>

        {renderChoiceSection("sauces", "Salsas", "Agregar salsa")}
        {renderChoiceSection("sides", "Acompañamientos", "Agregar acompañamiento")}
      </section>}

      {tab === "menu" && <section className="mesa-panel mesa-calendar-panel">
        <SectionHeader
          className="mesa-panel-head"
          title="Calendario"
          description="Revisá el mes de un vistazo. Abrí sólo el plato que necesitás modificar."
          actions={<button className="mesa-button" onClick={addWeek}>+ Agregar semana</button>}
        />
        {weeks.length === 0 && <div className="mesa-empty">Todavía no hay platos. Agregá una semana para empezar.</div>}
        <div className="mesa-week-stack">
          {weeks.map(([week, items]) => {
            const selectedInWeek = selectedMenuItem?.week === week ? selectedMenuItem : null;
            const firstDate = items.at(0)?.date ?? "";
            const lastDate = items.at(-1)?.date ?? "";
            return <section className={`mesa-week ${selectedInWeek ? "has-selection" : ""}`} key={week}>
              <div className="mesa-week-head">
                <div><span>Semana {week}</span><small>{firstDate && lastDate ? `${formatDate(firstDate)} — ${formatDate(lastDate)}` : "Sin fechas"}</small></div>
                <button type="button" className="mesa-text-action" onClick={() => duplicateWeek(week)}>Duplicar</button>
              </div>
              <div className="mesa-week-workspace">
                <div className="mesa-menu-list" role="list" aria-label={`Platos de la semana ${week}`}>
                  {items.map((item) => {
                    const active = selectedMenuItemId === item.id;
                    return <div className={`mesa-menu-row ${active ? "is-selected" : ""}`} role="listitem" key={item.id}>
                      <button type="button" className="mesa-menu-row-main" onClick={() => setSelectedMenuItemId(item.id)} aria-pressed={active}>
                        <span className="mesa-menu-date">{formatDate(item.date)}</span>
                        <span className="mesa-menu-copy"><strong>{item.dish || "Sin nombre"}</strong><small>{item.category || "Sin categoría"}</small></span>
                        <span className="mesa-menu-price">{formatMoney(item.price)}</span>
                        <span className="mesa-menu-state">
                          <span className={`mesa-state-label ${item.visible ? "is-live" : "is-muted"}`}>{item.visible ? "Visible" : "Oculto"}</span>
                          <small>{item.image ? "Con foto" : "Sin foto"}</small>
                        </span>
                        <span className="mesa-row-chevron" aria-hidden="true">›</span>
                      </button>
                    </div>;
                  })}
                </div>
                {selectedInWeek && renderMenuEditor(selectedInWeek)}
              </div>
            </section>;
          })}
        </div>
      </section>}

      {tab === "landing" && <section className="mesa-panel mesa-content-panel">
        <SectionHeader
          className="mesa-panel-head"
          title="Sitio público"
          description="Editá el mensaje que ve el cliente. El contenido está agrupado por función, no por orden técnico."
        />
        <div className="mesa-content-flow">
          <section className="mesa-content-section">
            <div className="mesa-content-section-head"><span>01</span><div><h3>Encabezado y propuesta</h3><p>Lo primero que entiende una persona al abrir el sitio.</p></div></div>
            <FormLayout className="mesa-form-grid">
              <label>Marca / encabezado<input value={draft.landing.eyebrow} onChange={(e) => setDraft({ ...draft, landing: { ...draft.landing, eyebrow: e.target.value } })} /></label>
              <label>Título principal<input value={draft.landing.title} onChange={(e) => setDraft({ ...draft, landing: { ...draft.landing, title: e.target.value } })} /></label>
              <label className="mesa-field-wide">Subtítulo<textarea rows={2} value={draft.landing.subtitle} onChange={(e) => setDraft({ ...draft, landing: { ...draft.landing, subtitle: e.target.value } })} /></label>
              <label className="mesa-field-wide">Introducción<textarea rows={2} value={draft.landing.intro} onChange={(e) => setDraft({ ...draft, landing: { ...draft.landing, intro: e.target.value } })} /></label>
            </FormLayout>
          </section>
          <section className="mesa-content-section">
            <div className="mesa-content-section-head"><span>02</span><div><h3>Explicación</h3><p>El argumento que explica por qué conviene planificar.</p></div></div>
            <FormLayout className="mesa-form-grid">
              <label>Título “La clave”<input value={draft.landing.keyTitle} onChange={(e) => setDraft({ ...draft, landing: { ...draft.landing, keyTitle: e.target.value } })} /></label>
              <label className="mesa-field-wide">Texto “La clave”<textarea rows={4} value={draft.landing.keyText} onChange={(e) => setDraft({ ...draft, landing: { ...draft.landing, keyText: e.target.value } })} /></label>
            </FormLayout>
          </section>
          <section className="mesa-content-section">
            <div className="mesa-content-section-head"><span>03</span><div><h3>Cómo funciona y beneficios</h3><p>Listas cortas que ayudan a decidir sin releer toda la página.</p></div></div>
            <FormLayout className="mesa-form-grid">
              <label>Cómo funciona <small>Una regla por línea</small><textarea rows={6} value={draft.rules.join("\n")} onChange={(e) => setDraft({ ...draft, rules: e.target.value.split("\n").map((line) => line.trim()).filter(Boolean) })} /></label>
              <label>Beneficios <small>Uno por línea</small><textarea rows={6} value={draft.benefits.join("\n")} onChange={(e) => setDraft({ ...draft, benefits: e.target.value.split("\n").map((line) => line.trim()).filter(Boolean) })} /></label>
            </FormLayout>
          </section>
          <section className="mesa-content-section">
            <div className="mesa-content-section-head"><span>04</span><div><h3>Cierre</h3><p>La frase final antes del contacto.</p></div></div>
            <FormLayout className="mesa-form-grid"><label className="mesa-field-wide">Frase final<input value={draft.landing.footer} onChange={(e) => setDraft({ ...draft, landing: { ...draft.landing, footer: e.target.value } })} /></label></FormLayout>
          </section>
        </div>
      </section>}

      {tab === "analytics" && <Mesa28Analytics organizationSlug={organization.organization.slug} appKey={app.appKey} />}

      {tab === "settings" && <section className="mesa-panel mesa-settings-panel">
        <SectionHeader
          className="mesa-panel-head"
          title="Ajustes"
          description="Contacto y publicación. Estos valores cambian cómo llega el cliente al negocio, no el contenido del menú."
        />
        <div className="mesa-settings-sections">
          <section className="mesa-settings-section">
            <div className="mesa-content-section-head"><span>01</span><div><h3>WhatsApp</h3><p>Destino y mensaje base para consultas generales.</p></div></div>
            <FormLayout className="mesa-form-grid">
              <label>Número de WhatsApp <small>Se conserva el número actual de MESA 28</small><input inputMode="numeric" placeholder="549351..." value={draft.whatsapp.number} onChange={(e) => setDraft({ ...draft, whatsapp: { ...draft.whatsapp, number: e.target.value.replace(/[^0-9]/g, "") } })} /></label>
              <label>Mensaje genérico<textarea rows={4} value={draft.whatsapp.message} onChange={(e) => setDraft({ ...draft, whatsapp: { ...draft.whatsapp, message: e.target.value } })} /></label>
            </FormLayout>
            <div className="mesa-settings-note"><strong>WhatsApp contextual</strong><p>Cuando una persona configura una milanesa, ClickDialog valida la salsa y el acompañamiento contra esta configuración y construye el mensaje en el servidor. El número no se toma de la web ni de la imagen del flyer.</p></div>
          </section>
          <section className="mesa-settings-section mesa-publication-section">
            <div className="mesa-content-section-head"><span>02</span><div><h3>Publicación</h3><p>El enlace es permanente y puede usarse en el QR físico.</p></div></div>
            {publicUrl ? <div className="mesa-public-link mesa-public-link-contextual">
              <div><strong>URL permanente</strong><code>{publicUrl}</code></div>
              <div className="mesa-public-link-actions"><button type="button" onClick={() => void copyPublicUrl(publicUrl)}>Copiar</button><a href={publicUrl} target="_blank" rel="noreferrer">Abrir</a></div>
            </div> : <div className="mesa-publication-empty"><strong>Todavía no hay un enlace público</strong><span>Generalo una vez y usalo para el QR permanente.</span><button type="button" onClick={createPublicLink}>Crear enlace público</button></div>}
          </section>
        </div>
      </section>}
    </div>
  );
}
